import {
  closestCenter,
  DndContext,
  pointerWithin,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
} from '@dnd-kit/core'
import { SortableContext, type SortingStrategy } from '@dnd-kit/sortable'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { DataTableDragPreview } from './DataTableDragPreview'
import { DataTableHeader } from './DataTableHeader'
import { DataTableRow } from './DataTableRow'
import { useTableDragScope } from './TableDragScope'
import {
  gutters,
  type DataTableProps,
  type DropTarget,
  type DropZone,
} from './types'
import { useColumnWidths } from './use-column-widths'
import { useRevealSelection } from './use-reveal-selection'
import { widenBranches } from './use-tree-expansion'
import {
  Callout,
  cn,
  columnVar,
  LoadingIndicator,
  Table,
  TableSkeleton,
  useDisabled,
} from '@/components/base'
import { EmptyState } from '@/components/EmptyState'
import { FilterToolbar } from '@/components/Internal/FilterToolbar'
import { PANEL_FRAME_CLASS } from '@/components/Internal/Panel'
import { useDragSensors } from '@/components/SortableItem'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import type { ClickModifiers } from '@/utils/click-modifiers'

/** Holds every row still while a drag is only choosing a drop target. */
const noReorderStrategy: SortingStrategy = () => null

/** How much of a row's height, top and bottom, means "beside it" where the
 *  middle means "inside it" — never thinner than a few pixels, so a dense row
 *  still has an edge a hand can hit. */
const EDGE_BAND = 0.3
const EDGE_MIN_PX = 6

/** The row's height in top-to-bottom order, so the bands read the way they
 *  are drawn. */
const ZONE_ORDER: readonly DropZone[] = ['before', 'into', 'after']

const BESIDE: readonly DropZone[] = ['before', 'after']
const INTO: readonly DropZone[] = ['into']
const NONE: readonly DropZone[] = []

/**
 * The row the pointer is over. `closestCenter` hands over the next row while
 * the pointer is still on this one, which would put a drop beside the row it
 * was meant for; it only decides where the pointer is between rows.
 */
const pointerCollision: CollisionDetection = args => {
  const under = pointerWithin(args)
  return under.length > 0 ? under : closestCenter(args)
}

/**
 * Which of the allowed zones the pointer sits in.
 *
 * The bands divide the row's height between exactly the zones the caller
 * allows, so a row with one allowed zone has no dead edge and a row with two
 * has no middle that means a third thing.
 */
function bandOf(
  pointerY: number,
  top: number,
  height: number,
  allowed: readonly DropZone[],
): DropZone | null {
  const zones = ZONE_ORDER.filter(zone => allowed.includes(zone))
  if (zones.length === 0) return null
  if (zones.length === 1) return zones[0]!

  const offset = pointerY - top
  if (zones.length === 2) return offset < height / 2 ? zones[0]! : zones[1]!

  const edge = Math.min(Math.max(height * EDGE_BAND, EDGE_MIN_PX), height / 3)
  if (offset < edge) return zones[0]!
  if (offset > height - edge) return zones[2]!
  return zones[1]!
}

export function DataTable<T>({
  items,
  columns,
  keyExtractor,
  isLoading = false,
  isError = false,
  emptyTitle,
  emptyDescription,
  emptyIcon = Search,
  toolbarItems,
  filters,
  hasActiveFilters,
  count,
  countLabel,
  toolbarExtra,
  sortState,
  onSortChange,
  card = true,
  fit = false,
  resizableColumns = true,
  columnWidthKey,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  onScroll,
  onRowPress,
  selectedRowKey,
  selectionKey,
  rowActions,
  selectionMode,
  selectedIds,
  onSelectionChange,
  rangeSelect = false,
  tree,
  expandedKeys: controlledExpandedKeys,
  onExpandedChange,
  ancestorsOf,
  sortable: sortableProp,
}: DataTableProps<T>) {
  // Dragging a row into another order changes what the surface refuses to let
  // anyone change, so a read-only scope takes the grip away — the way it takes
  // a `Button` and a `Menu`. Stated here rather than per screen: every table
  // in the app is this one.
  const sortable = useDisabled() ? undefined : sortableProp
  const hasActions = !!rowActions
  const selectable = selectionMode === 'multi'
  // `move` mode has no grip to show — `SortableItem` renders the whole row as
  // the drag target — so reserving a column for one leaves a dead gutter.
  const hasDragColumn = !!sortable && sortable.mode !== 'move'

  /**
   * The row's full column set, gutters included, so the placeholder holds the
   * grid the rows land on rather than only their content columns.
   */
  const columnGutters = useMemo(
    () =>
      gutters({
        drag: hasDragColumn,
        select: selectable,
        expand: !!tree,
        actions: hasActions,
      }),
    [hasDragColumn, selectable, tree, hasActions],
  )

  const { columnWidths, onResizeColumn } = useColumnWidths(
    resizableColumns,
    columnWidthKey,
  )

  /**
   * The dragged widths, as the properties every cell below reads. One
   * declaration on the scroller is what lets a drag move a column by writing
   * here rather than by rendering the list again.
   */
  const widthVars = useMemo(() => {
    if (!columnWidths) return undefined
    const style: Record<string, string> = {}
    for (const [key, px] of Object.entries(columnWidths)) {
      style[columnVar(key)] = `${px}px`
    }
    return style as CSSProperties
  }, [columnWidths])

  /**
   * Which property each resized column reads its width from, for the head, the
   * rows and the skeleton alike.
   *
   * Keyed on *which* columns have a width rather than on the widths, so
   * dragging one wider hands every row the same object and `memo` keeps them
   * — what changed reaches them through the property, not through React.
   */
  const sizedColumns = Object.keys(columnWidths ?? {})
    .sort()
    .join(' ')
  const columnVars = useMemo(() => {
    const names: Record<string, string> = {}
    for (const key of sizedColumns.split(' ').filter(Boolean)) {
      names[key] = columnVar(key)
    }
    return names
  }, [sizedColumns])

  const allKeys = useMemo(() => items.map(keyExtractor), [items, keyExtractor])

  // ─── Selection ────────────────────────────────────────────────────────────

  /**
   * Row key → what its checkbox stands for.
   *
   * A flat row stands for itself. A branch stands for whatever the caller says
   * it does — including rows a collapsed folder is hiding, which is why the
   * answer comes from the caller's own tree rather than from the rows the
   * table renders.
   */
  const keysByRow = useMemo(
    () =>
      !selectable
        ? new Map<string, string[]>()
        : new Map(
            items.map((item, index) => {
              const key = allKeys[index]!
              return [
                key,
                tree?.selectionKeys ? tree.selectionKeys(item) : [key],
              ]
            }),
          ),
    [items, allKeys, tree, selectable],
  )

  /** Every leaf the visible rows stand for, each counted once. */
  const selectableKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const row of keysByRow.values()) for (const key of row) keys.add(key)
    return [...keys]
  }, [keysByRow])

  const stateOf = useCallback(
    (keys: string[]): boolean | 'indeterminate' => {
      if (!selectedIds?.size || keys.length === 0) return false
      let picked = 0
      for (const key of keys) if (selectedIds.has(key)) picked += 1
      if (picked === 0) return false
      return picked === keys.length ? true : 'indeterminate'
    },
    [selectedIds],
  )

  const selectionState = useMemo(
    () => (selectable ? stateOf(selectableKeys) : false),
    [selectable, stateOf, selectableKeys],
  )

  // The click handlers below read the selection at click time, never during
  // render — through a ref, so their identity survives a tick and `memo` on
  // `DataTableRow` can bail out for every row that did not change.
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  const toggleKeys = useCallback(
    (keys: string[]) => {
      if (!onSelectionChange) return
      const next = new Set(selectedIdsRef.current)
      // A branch that is only partly ticked fills up rather than emptying —
      // the press means "all of these", and the half-state says it is not.
      if (keys.every(key => next.has(key)))
        keys.forEach(key => next.delete(key))
      else keys.forEach(key => next.add(key))
      onSelectionChange(next)
    },
    [onSelectionChange],
  )

  /** Ticks or clears everything one row stands for, in one go. */
  const toggleRow = useCallback(
    (key: string) => toggleKeys(keysByRow.get(key) ?? []),
    [toggleKeys, keysByRow],
  )

  /** The header's tick is the same rule over every visible row at once. */
  const toggleSelectAllVisible = useCallback(
    () => toggleKeys(selectableKeys),
    [toggleKeys, selectableKeys],
  )

  // The row a range extends *from*. A ref rather than state: it only matters at
  // the moment of the next click, and re-rendering the table because the anchor
  // moved would be pure waste.
  const rangeAnchor = useRef<string | null>(null)

  const selectRow = useCallback(
    (key: string, modifiers: ClickModifiers) => {
      if (!onSelectionChange) return
      const anchor = rangeAnchor.current
      const from = anchor === null ? -1 : allKeys.indexOf(anchor)
      const to = allKeys.indexOf(key)

      // No anchor, no shift, or an anchor that scrolled out of the loaded set:
      // a plain toggle beats selecting an arbitrary span.
      if (!rangeSelect || !modifiers.shift || from === -1 || to === -1) {
        rangeAnchor.current = key
        toggleRow(key)
        return
      }

      const [low, high] = from < to ? [from, to] : [to, from]
      onSelectionChange(
        new Set([
          ...selectedIdsRef.current!,
          ...allKeys.slice(low, high + 1).flatMap(k => keysByRow.get(k) ?? []),
        ]),
      )
    },
    [onSelectionChange, rangeSelect, allKeys, keysByRow, toggleRow],
  )

  // ─── Expansion ────────────────────────────────────────────────────────────

  const [ownExpandedKeys, setOwnExpandedKeys] = useState<Set<string>>(new Set())
  const isControlled = controlledExpandedKeys != null && !!onExpandedChange
  const expandedKeys = isControlled ? controlledExpandedKeys : ownExpandedKeys

  // Read at call time, so both writers below stay stable across a toggle.
  // `onToggleExpand` reaches every row through `DataTableRow`'s `memo`, and a
  // callback that changes identity on every expansion defeats it.
  const latest = useRef({
    expandedKeys,
    isControlled,
    onExpandedChange,
    ancestorsOf,
  })
  latest.current = { expandedKeys, isControlled, onExpandedChange, ancestorsOf }

  // Which of the two states a write lands in, asked in one place so every
  // writer below stays out of the controlled/uncontrolled question.
  const applyExpanded = useCallback(
    (update: (prev: ReadonlySet<string>) => Set<string> | null) => {
      const {
        expandedKeys: prev,
        isControlled,
        onExpandedChange,
      } = latest.current
      const next = update(prev)
      if (!next) return
      if (isControlled) onExpandedChange?.(next)
      else setOwnExpandedKeys(next)
    },
    [],
  )

  const toggleExpand = useCallback(
    (key: string) => {
      applyExpanded(prev => {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      })
    },
    [applyExpanded],
  )

  /**
   * Opens whatever holds the row a key names, so a selection arriving from a
   * link reaches a row that exists.
   *
   * Returns whether the branches are known at all — a tree still loading names
   * none, and that answer must not stand.
   */
  const openAncestors = useCallback(
    (selection: string) => {
      const ancestors = latest.current.ancestorsOf?.(selection) ?? []
      if (ancestors.length === 0) return false
      applyExpanded(prev => widenBranches(prev, ancestors))
      return true
    },
    [applyExpanded],
  )

  // Drop expanded ids that no longer name a row, so a refetch or a filter
  // change cannot leave a stale id expanding a fresh row it never belonged to.
  // A controlled caller owns its own keys; pruning them behind its back would
  // fight its state.
  useEffect(() => {
    if (isControlled) return
    const present = new Set(allKeys)
    setOwnExpandedKeys(prev => {
      const next = new Set([...prev].filter(key => present.has(key)))
      return next.size === prev.size ? prev : next
    })
  }, [isControlled, allKeys])

  // ─── Infinite scroll ──────────────────────────────────────────────────────

  // The selection is what a change of it is keyed on: with `isRowSelected` the
  // caller memoises it on exactly that.
  const revealRef = useRevealSelection(
    selectedRowKey,
    items.length,
    openAncestors,
  )

  const sentinelRef = useInfiniteScroll({
    hasNextPage: hasMore,
    isFetching: isLoadingMore,
    fetchNextPage: () => onLoadMore?.(),
    itemCount: items.length,
  })

  // ─── Drag and drop ────────────────────────────────────────────────────────

  const dragSensors = useDragSensors()

  // The row under the pointer and where in it the drop would land. Held as
  // state because it is drawn — a drag whose target is invisible is a guess.
  const [drop, setDrop] = useState<DropTarget | null>(null)
  const dropRef = useRef<DropTarget | null>(null)

  const itemByKey = useMemo(() => {
    const map = new Map<string, T>()
    items.forEach((item, index) => map.set(keyExtractor(item, index), item))
    return map
  }, [items, keyExtractor])

  /**
   * The drops a row takes where the caller states no rule. A row dragged in
   * from beside the table is judged only by a stated rule, so it takes
   * nothing here. A heading band takes what lands in its group; a row that
   * cannot be picked up is no sibling of the ones that can and takes nothing;
   * every other row takes a place on either side of it, so the last place of
   * a group is as reachable as the first.
   */
  const defaultDropZones = useCallback(
    (activeKey: string, row: T): readonly DropZone[] => {
      if (!itemByKey.has(activeKey)) return NONE
      if (tree?.heading?.(row)) return INTO
      return (sortable?.canDrag?.(row) ?? true) ? BESIDE : NONE
    },
    [tree, sortable, itemByKey],
  )

  const handleDragMove = useCallback(
    ({ active, over, delta, activatorEvent }: DragMoveEvent) => {
      if (!sortable) return
      if (!over || over.id === active.id) {
        dropRef.current = null
        setDrop(null)
        return
      }

      const overKey = String(over.id)
      const activeKey = String(active.id)
      const overRow = itemByKey.get(overKey)
      if (overRow === undefined) {
        dropRef.current = null
        setDrop(null)
        return
      }

      const allowed = sortable.dropZones
        ? sortable.dropZones(activeKey, overKey)
        : defaultDropZones(activeKey, overRow)
      const start = (activatorEvent as PointerEvent | undefined)?.clientY
      // Without a pointer there is no band to read — a keyboard drag takes the
      // first zone the pair allows, which is the move that needs no aim.
      const zone =
        start == null
          ? (allowed[0] ?? null)
          : bandOf(start + delta.y, over.rect.top, over.rect.height, allowed)

      const next = zone
        ? {
            key: overKey,
            zone,
            parentKey:
              sortable.dropParentKey?.(activeKey, overKey, zone) ?? null,
          }
        : null
      dropRef.current = next
      setDrop(current =>
        current?.key === next?.key &&
        current?.zone === next?.zone &&
        current?.parentKey === next?.parentKey
          ? current
          : next,
      )
    },
    [sortable, itemByKey, defaultDropZones],
  )

  const clearDrop = useCallback(() => {
    dropRef.current = null
    setDrop(null)
  }, [])

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      const landed = dropRef.current
      clearDrop()
      if (!sortable || !over || active.id === over.id) return
      // Only what the last move resolved to: a release over a row the rules
      // refused does nothing.
      if (landed?.key === String(over.id)) {
        sortable.onMove(String(active.id), landed.key, landed.zone)
      }
    },
    [sortable, clearDrop],
  )

  const handlers = useMemo(
    () => ({
      collisionDetection: pointerCollision,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onDragCancel: clearDrop,
    }),
    [handleDragMove, handleDragEnd, clearDrop],
  )
  const inScope = useTableDragScope(sortable ? handlers : null)

  // ─── Render ───────────────────────────────────────────────────────────────

  const table = (
    <Table ref={revealRef} onScroll={onScroll} style={widthVars} fit={fit}>
      <DataTableHeader
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        gutters={columnGutters}
        columnVars={columnVars}
        onResizeColumn={onResizeColumn}
        selectionState={selectionState}
        onToggleSelectAllVisible={toggleSelectAllVisible}
      />

      <>
        {isLoading ? (
          <TableSkeleton
            columns={[
              ...columnGutters.leading.map(gutter => ({
                width: gutter.className,
              })),
              ...columns.map(column => ({
                width: column.width,
                widthVar: columnVars[column.key],
              })),
              ...columnGutters.trailing.map(gutter => ({
                width: gutter.className,
              })),
            ]}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={emptyIcon}
            title={emptyTitle ?? CONTROLS.dataTable.empty}
            description={emptyDescription}
          />
        ) : (
          <>
            {items.map((item, index) => {
              const key = allKeys[index]!
              const isHeading = tree?.heading?.(item) ?? false
              const depth = tree?.depth?.(item) ?? 0
              return (
                <DataTableRow
                  key={key}
                  item={item}
                  columns={columns}
                  columnVars={columnVars}
                  index={index}
                  isSelected={
                    (selectionKey ? selectionKey(item) : key) === selectedRowKey
                  }
                  onRowPress={onRowPress}
                  rowActions={rowActions}
                  selectable={selectable}
                  rowKey={key}
                  isChecked={
                    selectable ? stateOf(keysByRow.get(key) ?? []) : false
                  }
                  // Passed whenever a row can be selected, not only under
                  // `rangeSelect`: the handler gates the range on shift itself,
                  // and ctrl-click has to work either way.
                  onSelectRow={selectable ? selectRow : undefined}
                  hasExpandColumn={!!tree}
                  isExpanded={expandedKeys.has(key)}
                  canExpand={tree?.hasChildren(item) ?? false}
                  onToggleExpand={toggleExpand}
                  depth={depth}
                  isHeading={isHeading}
                  sortable={!!sortable}
                  hasDragColumn={hasDragColumn}
                  // A band is never picked up, whatever `canDrag` says: it
                  // has no grip to pick it up by.
                  draggable={!isHeading && (sortable?.canDrag?.(item) ?? true)}
                  dragMode={sortable?.mode}
                  dropZone={drop?.key === key ? drop.zone : undefined}
                  dropIndent={depth}
                  isDropParent={drop?.parentKey === key}
                />
              )
            })}

            <div ref={sentinelRef} />

            {isLoadingMore ? (
              <LoadingIndicator
                layout="inline"
                size="small"
                text={CONTROLS.dataTable.loadMore}
              />
            ) : null}

            {onLoadMore && !hasMore ? (
              <p className="py-4 text-center text-sm text-faint">
                {CONTROLS.dataTable.allLoaded}
              </p>
            ) : null}
          </>
        )}
      </>
    </Table>
  )

  const sorting = sortable ? (
    <SortableContext
      items={allKeys}
      // Rows do not shuffle under the pointer: rows sliding past each other
      // say nothing about where the drop lands, which is what the marker is
      // for.
      strategy={noReorderStrategy}
    >
      {table}
    </SortableContext>
  ) : null

  // The drag context wraps the toolbar too, so a row dragged past the top of
  // the list still has a droppable under the pointer. Where a `TableDragScope`
  // already spans more of the screen, that one is the context and this table
  // only lends it its rules.
  const dragging = (
    <>
      {sorting}
      {sortable ? <DataTableDragPreview render={sortable.dragPreview} /> : null}
    </>
  )

  const body = !sorting ? (
    table
  ) : inScope ? (
    dragging
  ) : (
    <DndContext sensors={dragSensors} {...handlers}>
      {dragging}
    </DndContext>
  )

  const content = (
    <>
      {isError ? (
        <Callout tone="error" layout="bar">
          {COMMON.errors.loadFailed}
        </Callout>
      ) : null}
      <FilterToolbar
        toolbarItems={toolbarItems}
        hasActiveFilters={hasActiveFilters ?? filters?.hasActiveFilters}
        onResetFilters={filters?.reset}
        count={count}
        countLabel={countLabel}
        toolbarExtra={toolbarExtra}
      />
      {body}
    </>
  )

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        card ? PANEL_FRAME_CLASS : 'bg-card',
      )}
    >
      {content}
    </div>
  )
}
